// helper functions for common popup elements
import { 
    attachHelpText,
} from '../utils/popup-helpers.js';

/**
 * returns a URL object for same-origin referrer, or null if none/other-origin/invalid
 * @returns {URL} URL object
 */
export function getSameOriginReferrer() {
    let ref = document.referrer;
    if (!ref) return null;
    try {
        let u = new URL(ref);
        return (u.origin === location.origin) ? u : null;
    } catch {
        return null;
    }
}

/**
 * converts unix timestamp to MM/DD/YY string
 * @param {number} timestamp Unix timestamp in ms
 * @param {number} timezoneOffset user's timezone offset in min
 * @returns {string} MM/DD/YY
 */
export function timestampToFormalDate(timestamp, timezoneOffset) {
    // Adjust the timestamp for the user's timezone
    const adjustedTimestamp = timestamp - timezoneOffset * 60 * 1000;
    const date = new Date(adjustedTimestamp);
  
    // Use toLocaleDateString with 'UTC' timezone to format the adjusted date
    return date.toLocaleDateString('en-US', {
        timeZone: 'UTC',
        year: '2-digit',
        month: '2-digit',
        day: '2-digit'
    });
}

/**
 * calls mulitple functions in parallel and waits for all to complete.
 * functions with arguments must be objects with .fn (func name) and .args (arguments arr)
 * @param {array} functArr array of functions (with or without arguments)
 * @returns {array} resolved promises
 */
export async function callParallelFunctions(functArr) {
    // check 
    if (!Array.isArray(functArr)) {
        throw new Error('Expected an array of functions or function configs');
    }

    // create an array of promises by processing each function or function config
    let promises = functArr.map(item => {
        // Handle case where item is directly a function
        if (typeof item === 'function') {
            return item();
        }
        
        // Handle case where item is a function config object with fn and args properties
        if (item && typeof item === 'object' && typeof item.fn === 'function') {
            return item.fn(...(item.args || []));
        }        
    });
    
    // wait for all promises to resolve
    return Promise.all(promises);
}

/**
 * Scrolls to a specific element within a scrollable container.
 * @param {HTMLElement} selectedElement - The element to scroll to
 * @param {HTMLElement} containerElement - The scrollable container element
 * @param {Object} options - Scroll options
 * @param {number} options.offsetTop - Additional offset from the top (default: 0)
 * @param {string} options.behavior - Scroll behavior: 'auto', 'smooth' (default: 'smooth')
 * @param {string} options.block - Vertical alignment: 'start', 'center', 'end', 'nearest' (default: 'start')
 * @returns {boolean} - Returns true if scroll was successful, false otherwise
 */
export function scrollToElement(selectedElement, containerElement, options = {}) {
    // Validate inputs
    if (!selectedElement || !(selectedElement instanceof HTMLElement)) { // destination element
        console.error('Invalid selectedElement: Must be a valid DOM element');
        return false;
    }
    if (!containerElement || !(containerElement instanceof HTMLElement)) { // container
        console.error('Invalid containerElement: Must be a valid DOM element');
        return false;
    }
    if (!containerElement.contains(selectedElement)) { // relationship
        console.error('The selected element is not a descendant of the container element');
        return false;
    }
    
    // Default options
    const defaultOptions = {
        offsetTop: 20,
        behavior: 'smooth',
        block: 'start'
    };
    
    // Merge default options with provided options
    const scrollOptions = { ...defaultOptions, ...options };
    
    try {
        // Use scrollIntoView with the container's scrolling context if supported
        if (typeof selectedElement.scrollIntoView === 'function') {
            // Calculate the relative position for custom offset
            if (scrollOptions.offsetTop !== 0) {
                // Get positions relative to the page
                const containerRect = containerElement.getBoundingClientRect();
                const elementRect = selectedElement.getBoundingClientRect();
                
                // Calculate the scroll position
                const scrollTop = elementRect.top - containerRect.top - scrollOptions.offsetTop + containerElement.scrollTop;
                
                // Perform the scroll
                containerElement.scrollTo({
                    top: scrollTop,
                    behavior: scrollOptions.behavior
                });
            } else {
                // Use native scrollIntoView for standard scrolling
                selectedElement.scrollIntoView({
                    behavior: scrollOptions.behavior,
                    block: scrollOptions.block
                });
                
                // Fix for scrollIntoView when used inside a scrollable container
                // This ensures the container scrolls, not the whole page
                if (containerElement !== document.documentElement && containerElement !== document.body) {
                    const elementPos = selectedElement.offsetTop;
                    containerElement.scrollTop = elementPos - scrollOptions.offsetTop;
                }
            }
            
            return true;
        } else {
            // Fallback for browsers that don't support scrollIntoView
            const elementPos = selectedElement.offsetTop;
            containerElement.scrollTop = elementPos - scrollOptions.offsetTop;
            return true;
        }
    } catch (error) {
        console.error('Error scrolling to element:', error);
        
        // Last resort fallback
        try {
            containerElement.scrollTop = selectedElement.offsetTop - scrollOptions.offsetTop;
            return true;
        } catch (e) {
            console.error('Fallback scroll also failed:', e);
            return false;
        }
    }
}

/**
 * capitalizes first letter of first word
 * @param {string} str word/phrase to capitalize
 * @outputs string
 */
export function capFirstLetter(str) {
    return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * formats unix ms due date into due string
 * @param {number} timestamp Unix timestamp in ms
 * @param {number} timezoneOffset timzeone offset from UTC in min
 * @returns due string
 */
export function dateToDueFormat(timestamp, timezoneOffset) {
    // normalize everything to UTC-ms
    const now = Date.now();
    // adjust the raw timestamp by the user’s timezone offset (in minutes)
    const dueTime = timestamp - timezoneOffset * 60_000;
    const diff = dueTime - now;

    if (diff < 0) {
        return "Overdue";
    }

    // under 1 hour → minutes left
    if (diff < 60 * 60 * 1000) {
        const mins = Math.ceil(diff / (60 * 1000));
        return `${mins} min`;
    }

    // under 24 hours → hours left
    if (diff < 24 * 60 * 60 * 1000) {
        const hrs = Math.ceil(diff / (60 * 60 * 1000));
        return `${hrs} hours`;
    }

    // under 7 days → days left
    if (diff < 7 * 24 * 60 * 60 * 1000) {
        const days = Math.ceil(diff / (24 * 60 * 60 * 1000));
        return `${days} days`;
    }

    // otherwise → weeks left
    const weeks = Math.ceil(diff / (7 * 24 * 60 * 60 * 1000));
    return `${weeks} weeks`;
}

/**
 * converts hex code color to RGBA value
 * @param {string} originalColor hex code color (e.g. #1419fc or white)
 * @param {number} transparency transp decimal (0 to 1)
 * @returns {string} RBGA value (with opacity)
 */
export function convertToRGBA(originalColor, transparency) {
    // helper to parse a hex string to {r,g,b}
    function hexToRgb(hex) {
        hex = hex.replace(/^#/, '');
        if (hex.length === 3) hex = hex.split('').map(h => h+h).join('');
        let num = parseInt(hex, 16);
        return { r: num >> 16, g: (num >> 8) & 0xff, b: num & 0xff };
    }
  
    let r, g, b;
    if (originalColor[0] === '#') {
        // hex input
        ({ r, g, b } = hexToRgb(originalColor));
    } else {
        // any other valid CSS color (named, rgb(), etc.)
        let div = document.createElement('div');
        div.style.color = originalColor;
        document.body.appendChild(div);
        let rgbString = getComputedStyle(div).color; // e.g. "rgb(255, 255, 255)"
        document.body.removeChild(div);
        [r, g, b] = rgbString.match(/\d+/g).map(Number);
    }
  
    return `rgba(${r}, ${g}, ${b}, ${transparency})`;
}

/**
 * creates level text from raw score
 * @param {number} score raw score
 * @param {string} typeKey level type (short or long)
 * @param {boolean} onlyLvl return only rank (not subrank)
 * @param {boolean} onlySub return only subrank (not rank)
 * @returns {string} level text
 */
export function scoreLvl(score, typeKey, onlyLvl = false, onlySub = false) {
    // markers
    let levelMarkers = [200, 400, 600, 800, 1000];
    let levelNames = [ // what to call each lvl
        {
            long: `Fogged`,
            short: `Fg`,
        },
        {
            long: `Focused`,
            short: `Fc`,
        },
        {
            long: `Insightful`,
            short: `Is`,
        },
        {
            long: `Illuminated`,
            short: `Im`,
        },
        {
            long: `Visionary`,
            short: `V`,
        },
    ];
    let levelRankNames = [ // what to call each sub-lvl
        {
            short: `I`,
        },
        {
            short: `II`,
        },
        {
            short: `III`,
        },
    ];

    // zero case
    if (score == 0) {
        return `None`;
    }

    // loop over levels
    for (let levelM of levelMarkers) {
        // if in level
        if (score < levelM) {
            // if only return level name (not sublevel)
            if (onlyLvl) {
                return `${levelNames[levelMarkers.indexOf(levelM)][typeKey]}`;
            }

            // loop over sub levels (1-3)
            for (let i = 1; i < 4; i++) {
                // if in sub level
                if ((score % 200) <= (200 / 3 * i)) {
                    // if only sub
                    if (onlySub) {
                        return `${levelRankNames[i - 1][`short`]}`;
                    }

                    return `${levelNames[levelMarkers.indexOf(levelM)][typeKey]} ${levelRankNames[i - 1][`short`]}`;
                }
            }
        }
    }

    // error case
    return `Error`;
}

/**
 * renders a radar graph
 * @param {number} score score from 0-100
 * @param {string} divID radar div ID
 * @param {string} mainColor color of radar arc
 * @param {string} backgroundColor color of background arc
 */
export async function generateRadarChart(score, divID, mainColor, backgroundColor) {
    // container and base settings
    const ratioWH = 1.565;
    const tolerance = 0.1;
    const graphDiv = document.getElementById(divID);
    graphDiv.innerHTML = ``; // clear
    const realW = graphDiv.clientWidth;
    const realH = graphDiv.clientHeight;
    const actualRatio = realW / realH;
    const baseAngle = Math.PI / 2;

    let circleDiameter;
    let startAngle, endAngle;

    // choose sizing and angles based on aspect ratio
    if (Math.abs(actualRatio - ratioWH) <= tolerance) {
        // ratio-based semi-circle + padding
        const adjW = realH * ratioWH;
        circleDiameter = (adjW > realW) ? realW : adjW;
        startAngle = -baseAngle - 0.1 * Math.PI;
        endAngle = baseAngle + 0.1 * Math.PI;
    } else {
        // width defines full diameter, height < width: fill height via atan
        circleDiameter = realW;
        const radius = circleDiameter / 2;
        // compute theta so arc fits full height: height_offset = height - radius
        const theta = Math.atan((realH - radius) / radius);
        startAngle = -baseAngle - theta - (Math.PI / 20);
        endAngle = baseAngle + theta + (Math.PI / 20);
    }

    // radii and thickness
    const radius = circleDiameter / 2;
    const thickness = (circleDiameter / 20) + 3;
    const innerRadius = radius - thickness;

    // define D3 arcs
    const backgroundArc = d3.arc()
        .innerRadius(innerRadius)
        .outerRadius(radius)
        .cornerRadius(20)
        .startAngle(startAngle)
        .endAngle(endAngle);

    const mainArc = d3.arc()
        .innerRadius(innerRadius)
        .outerRadius(radius)
        .cornerRadius(20)
        .startAngle(startAngle)
        .endAngle(d => startAngle + (d.value / 100) * (endAngle - startAngle));

    // create SVG matching div dimensions
    const svg = d3.select(`#${divID}`)
        .append('svg')
        .attr('width', realW)
        .attr('height', realH)
        .style('overflow', 'visible');

    // center arc group at bottom-center of SVG
    const data = [{ label: 'Data', value: score }];
    const g = svg.selectAll('g.radar-group')
        .data(data)
        .enter()
        .append('g')
        .attr('class', 'radar-group')
        // center horizontally, shift vertically so arc sits within container
        .attr('transform', `translate(${realW/2}, ${realH/2 - (realH - realW)/2 + thickness/2})`);

    // draw background and data arcs
    g.append('path')
        .attr('d', backgroundArc)
        .attr('fill', backgroundColor);

    g.append('path')
        .attr('d', mainArc)
        .attr('fill', mainColor);
}

/**
 * sets rank colors and icon svg
 * @param {number} score raw score
 * @param {object} rankObj
 * @property {Element} icon icon div
 * @property {Element} div overall div
 * @property {Element} numText short num txt
 * @returns nothing
 */
export function setRankColorIcon(rankObj, score, rankThemes) {
    // markers
    let levelMarkers = [200, 400, 600, 800, 1000];

    // loop over levels
    for (let levelM of levelMarkers) {

        // if in level
        if (score < levelM) {
            // theme colors
            let color = rankThemes.colors[levelMarkers.indexOf(levelM) + 1];

            // set icon body color
            rankObj.icon.style.color = color.highlight;

            // set gradient background color
            rankObj.div.classList.add(color.className);

            // set text color
            [ rankObj.numText, ].forEach(ele => { ele.style.color = color.contrast });

            // loop over sub levels (1-3)
            for (let i = 1; i < 4; i++) {

                // if in sub level
                if ((score % 200) <= (200 / 3 * i)) {

                    // get icon svg
                    let svg = rankThemes.crest[i];

                    // in sub-rank 3, edit REPLACETHIS with contrast theme
                    if (i == 3) {
                        svg = svg.replaceAll("REPLACETHIS", color.contrast);
                    }

                    // set icon
                    rankObj.icon.innerHTML = svg;

                    // set help text
                    attachHelpText(rankObj.div, `${rankThemes.names[levelMarkers.indexOf(levelM) + 1]} ${"I".repeat(i)}`, `auto`);

                    return;
                }
            }
        }
    }
}

/**
 * delay until next line of code
 * @param {number} ms milliseconds
 * @returns promise
 */
export async function delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}
 
/**
 * Format a number as currency.
 *
 * @param {number|string|bigint} value - Amount to format.
 * @param {object} [opts]
 * @param {string} [opts.locale="en-US"] - BCP-47 locale tag, e.g. "en-US", "de-DE", "ja-JP".
 * @param {string} [opts.currency="USD"] - ISO 4217 code, e.g. "USD", "EUR", "JPY".
 * @param {number} [opts.minimumFractionDigits] - Min fraction digits.
 * @param {number} [opts.maximumFractionDigits] - Max fraction digits.
 * @param {boolean} [opts.useGrouping=true] - Use thousands separators.
 * @param {"standard"|"scientific"|"engineering"|"compact"} [opts.notation="standard"]
 * @param {boolean} [opts.trimZeros=false] - Remove trailing decimal zeros (e.g., $2.50 → $2.5).
 * @returns {string}
 */
export function formatCurrency(value, opts = {}) {
    const {
        locale = "en-US",
        currency = "USD",
        minimumFractionDigits,
        maximumFractionDigits,
        useGrouping = true,
        notation = "standard",
        trimZeros = false,
    } = opts;

    const num =
        typeof value === "bigint" ? Number(value) :
        typeof value === "string" ? Number(value.trim()) :
        Number(value);

    if (!Number.isFinite(num)) throw new TypeError("Invalid number");

    const options = {
        style: "currency",
        currency,
        useGrouping,
        notation,
    };
    if (minimumFractionDigits != null) options.minimumFractionDigits = minimumFractionDigits;
    if (maximumFractionDigits != null) options.maximumFractionDigits = maximumFractionDigits;

    const nf = new Intl.NumberFormat(locale, options);

    if (!trimZeros) return nf.format(num);

    // Trim trailing zeros using formatToParts (locale-safe).
    const parts = nf.formatToParts(num);
    const i = parts.findIndex(p => p.type === "fraction");
    if (i >= 0) {
        const trimmed = parts[i].value.replace(/0+$/, "");
        if (trimmed === "") {
            // Remove fraction and preceding decimal symbol.
            parts.splice(i, 1);
            if (i - 1 >= 0 && parts[i - 1].type === "decimal") {
                parts.splice(i - 1, 1);
            }
        } else {
            parts[i].value = trimmed;
        }
    }
    return parts.map(p => p.value).join("");
}

/**
 * formats past unix ms due date into relative time string (e.g. now, 1 min ago, 5 hours ago, 2 weeks ago)
 * @param {number} timestamp Unix timestamp in ms (less than now)
 * @param {number} timezoneOffset timzeone offset from UTC in min
 * @returns due string
 */
export function dateToAgoFormat(timestamp, timezoneOffset) {
    // normalize everything to UTC-ms
    const now = Date.now();
    // adjust the raw timestamp by the user’s timezone offset (in minutes)
    const dueTime = timestamp - timezoneOffset * 60_000;
    const diff = now - dueTime;

    if (diff < 0) {
        return "Now";
    }

    // under 1 hour → minutes left
    if (diff < 60 * 60 * 1000) {
        const mins = Math.ceil(diff / (60 * 1000));
        return `${mins} min ago`;
    }

    // under 24 hours → hours left
    if (diff < 24 * 60 * 60 * 1000) {
        const hrs = Math.ceil(diff / (60 * 60 * 1000));
        return `${hrs} hours ago`;
    }

    // under 7 days → days left
    if (diff < 7 * 24 * 60 * 60 * 1000) {
        const days = Math.ceil(diff / (24 * 60 * 60 * 1000));
        return `${days} days ago`;
    }

    // otherwise → weeks left
    const weeks = Math.ceil(diff / (7 * 24 * 60 * 60 * 1000));
    return `${weeks} weeks ago`;
}